import React, { useState, useEffect } from 'react';
import { Form, Button, Table, Modal, Alert, Tabs, Tab, Spinner } from 'react-bootstrap';
import { AiOutlinePlus, AiOutlineDelete } from 'react-icons/ai';
import axios from '../utils/api';
import './AdminDashboard.css';

function SkillsEditor() {
  const [techSkills, setTechSkills] = useState([]);
  const [toolSkills, setToolSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentType, setCurrentType] = useState('tech');
  const [, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: '',
    type: 'tech'
  });

  useEffect(() => {
    fetchSkills('tech');
    fetchSkills('tool');
  }, []);

  const fetchSkills = async (type) => {
    try {
      const response = await axios.get(`/content/simple-content?type=skills&subtype=${type}`);
      const skills = response.data.data || [];
      if (type === 'tech') {
        setTechSkills(skills);
      } else {
        setToolSkills(skills);
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to load skills');
      setLoading(false);
    }
  };

  const handleOpenModal = (type, skill = null) => {
    setCurrentType(type);
    if (skill) {
      setEditingSkill(skill);
      setFormData({
        name: skill.name || '',
        icon: skill.icon || '',
        type: skill.type || type
      });
    } else {
      setEditingSkill(null);
      setFormData({
        name: '',
        icon: '',
        type: type
      });
    }
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSkill(null);
    setFormData({ name: '', icon: '', type: currentType });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await axios.post('/content/simple-content?type=skills', formData);
      setSuccess('Skill added successfully');
      handleCloseModal();
      fetchSkills(formData.type);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save skill');
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this skill?')) {
      return;
    }

    try {
      await axios.delete('/content/simple-content?type=skills', { data: { id } });
      setSuccess('Skill deleted successfully');
      fetchSkills(type);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete skill');
    }
  };

  const renderSkillsTable = (skills, type) => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 style={{ color: '#333', fontWeight: '600' }}>{type === 'tech' ? '💻 Technical Skills' : '🔧 Tools'}</h4>
        <Button className="btn-primary-admin" onClick={() => handleOpenModal(type)}>
          <AiOutlinePlus style={{ marginRight: '0.5rem' }} />
          Add {type === 'tech' ? 'Skill' : 'Tool'}
        </Button>
      </div>

      {skills.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📭</div>
          <p>No {type === 'tech' ? 'skills' : 'tools'} found. Add your first one!</p>
        </div>
      ) : (
        <Table className="admin-table" responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Icon</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {skills.map((skill) => (
              <tr key={skill.id}>
                <td><strong>{skill.name}</strong></td>
                <td><code style={{ background: '#f0f0f0', padding: '0.25rem 0.5rem', borderRadius: '5px' }}>{skill.icon}</code></td>
                <td>
                  <Button 
                    className="btn-danger-admin" 
                    size="sm"
                    onClick={() => handleDelete(skill.id, type)}
                  >
                    <AiOutlineDelete style={{ marginRight: '0.25rem' }} />
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );

  return (
    <div className="editor-card">
      {error && <Alert variant="danger" className="alert-admin" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" className="alert-admin" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <div className="editor-header">
        <h3>🛠️ Manage Skills</h3>
      </div>

      <Tabs defaultActiveKey="tech" className="mb-3" style={{ borderBottom: '2px solid #f0f0f0' }}>
        <Tab eventKey="tech" title="💻 Technical Skills">
          {loading ? (
            <div className="loading-spinner">
              <Spinner animation="border" />
            </div>
          ) : renderSkillsTable(techSkills, 'tech')}
        </Tab>
        <Tab eventKey="tool" title="🔧 Tools">
          {loading ? (
            <div className="loading-spinner">
              <Spinner animation="border" />
            </div>
          ) : renderSkillsTable(toolSkills, 'tool')}
        </Tab>
      </Tabs>

      <Modal show={showModal} onHide={handleCloseModal} className="admin-modal">
        <Modal.Header closeButton>
          <Modal.Title>➕ Add {currentType === 'tech' ? 'Technical Skill' : 'Tool'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} className="admin-form">
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., React, Python, Docker"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Icon (React Icons name) *</Form.Label>
              <Form.Control
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                required
                placeholder="e.g., DiReact, FaPython, SiDocker"
              />
              <Form.Text className="text-muted">
                Use React Icons component names (e.g., DiReact, FaGithub)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type *</Form.Label>
              <Form.Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
              >
                <option value="tech">Technical Skill</option>
                <option value="tool">Tool</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} style={{ borderRadius: '10px' }}>
              Cancel
            </Button>
            <Button className="btn-primary-admin" type="submit">
              ✨ Add
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default SkillsEditor;

